import {
  Tab as ReactAriaTab,
  TabList as ReactAriaTabList,
  TabPanel as ReactAriaTabPanel,
  Tabs as ReactAriaTabs,
  type TabListProps as ReactAriaTabListProps,
  type TabPanelProps as ReactAriaTabPanelProps,
  type TabProps as ReactAriaTabProps,
  type TabsProps as ReactAriaTabsProps,
} from "react-aria-components";

import { useI18n } from "../use-i18n";
import { cn, cv, type VariantProps } from "../utils";
import { NcFormattedMessage } from "./nc-formatted-message";
import { NcLoadingIndicator } from "./nc-loading-indicator";

const NcTabs = ({ className, ...props }: ReactAriaTabsProps) => (
  <ReactAriaTabs
    data-nc="NcTabs"
    className={cn(
      "group flex min-h-0 w-full grow flex-col rounded outline-none data-[orientation='vertical']:flex-row",
      className
    )}
    {...props}
  />
);

const TabList = <T extends object>({ className, ...props }: ReactAriaTabListProps<T>) => (
  <ReactAriaTabList
    data-nc="NcTabs.TabList"
    className={cn(
      "flex shrink-0 gap-2 data-[orientation='horizontal']:px-2 data-[orientation='vertical']:-mr-px data-[orientation='vertical']:flex-col",
      className
    )}
    {...props}
  />
);

const Tab = ({ className, ...props }: ReactAriaTabProps) => (
  <ReactAriaTab
    data-nc="NcTabs.Tab"
    className={cn(
      "bg-light border-ui relative flex justify-between overflow-hidden rounded-sm border font-bold whitespace-normal text-inherit no-underline transition-colors not-data-selected:cursor-pointer",
      "data-selected:text-main data-selected:before:bg-active data-selected:before:absolute data-selected:before:top-0",
      "data-disabled:text-disabled data-disabled:bg-ui-light data-disabled:font-normal",
      "data-focus-visible:outline-main data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
      "data-hovered:not-data-selected:bg-main data-hovered:not-data-selected:text-light",
      "group-data-[orientation='horizontal']:-mb-px group-data-[orientation='horizontal']:basis-full group-data-[orientation='horizontal']:rounded-b-none group-data-[orientation='horizontal']:p-3",
      "group-data-[orientation='horizontal']:data-selected:border-b-light group-data-[orientation='horizontal']:data-selected:before:right-0 group-data-[orientation='horizontal']:data-selected:before:left-0 group-data-[orientation='horizontal']:data-selected:before:h-1",
      "group-data-[orientation='vertical']:rounded-r-none group-data-[orientation='vertical']:p-4",
      "group-data-[orientation='vertical']:data-selected:border-r-light group-data-[orientation='vertical']:data-selected:before:bottom-0 group-data-[orientation='vertical']:data-selected:before:left-0 group-data-[orientation='vertical']:data-selected:before:w-1",
      className
    )}
    {...props}
  />
);

interface NcTabPanelProps extends ReactAriaTabPanelProps, VariantProps<typeof ncTabPanelVariants> {
  error?: boolean | string;
  empty?: boolean | string;
  isLoading?: boolean;
}

type NcTabPanelState = "LOADING" | "LOADED" | "EMPTY" | "ERROR";

function getPanelState({
  isLoading,
  empty,
  error,
}: Omit<NcTabPanelProps, "children">): NcTabPanelState {
  if (isLoading) return "LOADING";
  if (empty) return "EMPTY";
  if (error) return "ERROR";
  return "LOADED";
}

const ncTabPanelVariants = cv(
  "border-ui flex flex-col grow p-4 bg-light overflow-y-auto data-focus-visible:z-1 data-[orientation='vertical']:border-t-0 data-[orientation='vertical']:border-l-0 rounded data-focus-visible:outline-main data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
  {
    variants: {
      variant: {
        default: "border",
        subPanel:
          "group-data-[orientation='horizontal']:border-t group-data-[orientation='vertical']:border-l",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const TabPanel = ({
  children,
  isLoading,
  empty,
  error,
  variant = "default",
  id,
  className,
  ...props
}: NcTabPanelProps) => {
  const state = getPanelState({ isLoading, empty, error });
  if (state === "LOADED") {
    return (
      <ReactAriaTabPanel
        data-nc="NcTabs.TabPanel"
        className={cn(ncTabPanelVariants({ variant }), className)}
        id={id}
        {...props}
      >
        {children}
      </ReactAriaTabPanel>
    );
  }
  return (
    <ReactAriaTabPanel
      data-nc="NcTabs.TabPanel"
      className={ncTabPanelVariants({ variant })}
      id={id}
    >
      {state === "LOADING" && (
        <div className="text-brand flex w-full grow items-center justify-center">
          <NcLoadingIndicator />
        </div>
      )}
      {state === "ERROR" && <TabPanelError error={error} />}
      {state === "EMPTY" && <TabPanelEmpty empty={empty} />}
    </ReactAriaTabPanel>
  );
};

const TabPanelError = ({ error }: { error: NcTabPanelProps["error"] }) => {
  const { t } = useI18n();
  const errorText = typeof error === "string" ? error : t("error_default");
  return <NcFormattedMessage variant="danger">{errorText}</NcFormattedMessage>;
};

const TabPanelEmpty = ({ empty }: { empty: NcTabPanelProps["empty"] }) => {
  const { t } = useI18n();
  const emptyText = typeof empty === "string" ? empty : t("empty_default");
  return <NcFormattedMessage variant="secondary">{emptyText}</NcFormattedMessage>;
};

NcTabs.TabList = TabList;
NcTabs.Tab = Tab;
NcTabs.TabPanel = TabPanel;

export { NcTabs };
